// A simple search box for list pages. Filters happen client-side on
// whatever fields the parent picks; this component owns nothing but
// the input value + a clear button. Keeping it dumb means we don't
// debounce here — list pages with hundreds of rows already render
// fine for substring filtering, and the input feels snappier without
// a debounce when typing slowly.

import { Icon } from '@ng-unifi/ui';

export interface SearchInputProps {
  /** Current search string. */
  value: string;
  /** Called with the new string on every keystroke. */
  onChange: (next: string) => void;
  /** Placeholder hint — e.g. "Search by name or email…". */
  placeholder?: string;
  /** Accessible label; defaults to the placeholder. */
  ariaLabel?: string;
  /** Optional id so a parent can render its own <label htmlFor>. */
  id?: string;
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  ariaLabel,
  id,
}: SearchInputProps) {
  const inputId = id ?? 'search-input';
  return (
    <div className="relative flex-1 max-w-md">
      <span
        aria-hidden="true"
        className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant"
      >
        <Icon name="search" />
      </span>
      <input
        id={inputId}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        className="w-full rounded-lg border border-outline bg-surface-container-lowest pl-10 pr-9 py-2 font-body-md text-body-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
      />
      {value.length > 0 ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-6 h-6 rounded-full text-on-surface-variant hover:bg-surface-container-high"
        >
          <Icon name="close" className="text-sm" />
        </button>
      ) : null}
    </div>
  );
}
